import useGoalChangeRepo, { GOAL_CHANGE_REFRESH_DELAY } from '@/composables/goal-change/goal-change-repo';
import useSubscription from '@/composables/subscription/subscription';
import { goalChange as goalChangeConfig } from 'shared/api/query/configs.json';
import { logCatch } from '@/lib/logger/logger';
import { onBeforeUnmount, watch } from 'vue';
import { subscriptionAction } from 'shared/constants.json';

// Only while a history panel is open instead of at login: create events don't name the goal, so every change created
// in the account is fetched, filtered to the open goal.
export default function useGoalChangeDataSync(goalId) {
  const goalChangeRepo = useGoalChangeRepo();
  const { subscribe } = useSubscription();

  const toFetch = new Set();
  let timer = null;

  const loaded = (ids) => {
    const uids = new Set(goalChangeRepo.changesOf(goalId.value).map((c) => String(c.uid)));
    return ids.filter((id) => uids.has(String(id)));
  };

  const fetch = () => {
    const ids = [...toFetch];
    toFetch.clear();
    const goal = goalId.value;
    return goalChangeRepo.getByIds({ goalId: goal, ids })
      .catch(logCatch(() => {
        if (goal === goalId.value) {
          ids.forEach((id) => toFetch.add(id));
        }
      }));
  };

  // Autosave merges into the latest change every few hundred milliseconds while typing.
  const scheduleFetch = () => {
    clearTimeout(timer);
    timer = setTimeout(fetch, GOAL_CHANGE_REFRESH_DELAY);
  };

  subscribe({
    model: goalChangeConfig.model,
    includeSelfEvents: true,
    syncFn: ({ action, ids }) => {
      switch (action) {
        case subscriptionAction.create:
          ids.forEach((id) => toFetch.add(id));
          scheduleFetch();
          break;
        case subscriptionAction.update: {
          const refresh = loaded(ids);
          if (refresh.length > 0) {
            refresh.forEach((id) => toFetch.add(id));
            scheduleFetch();
          }
          break;
        }
        case subscriptionAction.delete:
          ids.forEach((id) => toFetch.delete(id));
          goalChangeRepo.removeLocally(loaded(ids));
          break;
        default:
          break;
      }
    },
  });

  watch(goalId, () => {
    clearTimeout(timer);
    toFetch.clear();
  });

  onBeforeUnmount(() => clearTimeout(timer));
}
